import { useLocation, useNavigate } from 'react-router-dom';
import type { Language } from '../types';
import {
  LANGUAGE_CONFIG,
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
} from '../types/translations';
import { useContent } from '../context/ContentContext';

// Re-export static constants for convenience
export { LANGUAGE_CONFIG, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE };

export function useLanguage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { siteLanguages } = useContent();

  // Non-default active language codes from DB; falls back to static ['he'] if DB list is empty
  const supportedCodes: string[] =
    siteLanguages.filter((l) => !l.isDefault && l.isActive).map((l) => l.code).length > 0
      ? siteLanguages.filter((l) => !l.isDefault && l.isActive).map((l) => l.code)
      : SUPPORTED_LANGUAGES;

  // Determine current language from URL pathname
  const getLangFromPath = (pathname: string): Language => {
    const firstSegment = pathname.split('/')[1] ?? '';
    if (firstSegment && supportedCodes.includes(firstSegment)) {
      return firstSegment as Language;
    }
    return DEFAULT_LANGUAGE;
  };

  const currentLang: Language = getLangFromPath(location.pathname);

  // Generate a localized path
  const localizedPath = (path: string, targetLang?: Language): string => {
    const language = targetLang ?? currentLang;

    let cleanPath = path.startsWith('/') ? path.slice(1) : path;

    // Strip any existing language prefix
    for (const code of supportedCodes) {
      if (cleanPath.startsWith(`${code}/`)) {
        cleanPath = cleanPath.slice(code.length + 1);
        break;
      } else if (cleanPath === code) {
        cleanPath = '';
        break;
      }
    }

    if (language === DEFAULT_LANGUAGE) {
      return cleanPath ? `/${cleanPath}` : '/';
    }
    return cleanPath ? `/${language}/${cleanPath}` : `/${language}`;
  };

  const getPathWithoutLang = (): string => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const first = pathParts[0];
    if (first && supportedCodes.includes(first)) {
      pathParts.shift();
    }
    return '/' + pathParts.join('/');
  };

  const switchLanguage = (targetLang: Language) => {
    const basePath = getPathWithoutLang();
    navigate(localizedPath(basePath, targetLang) + location.hash);
  };

  const toggleLanguage = () => {
    const newLang = currentLang === 'he' ? 'en' : 'he';
    switchLanguage(newLang);
  };

  // isRTL: check static config first, then DB
  const staticConfig = LANGUAGE_CONFIG[currentLang as Language];
  const dbConfig = siteLanguages.find((l) => l.code === currentLang);
  const isRTL: boolean = staticConfig?.isRTL ?? dbConfig?.isRtl ?? false;

  return {
    lang: currentLang,
    localizedPath,
    switchLanguage,
    toggleLanguage,
    isRTL,
  };
}
